import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const PRIORITY_COLORS = { High: 'error', Medium: 'warning', Low: 'success' };
const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

/**
 * List view for projects: table with pagination (page + rows per page).
 * Optionally supports "Load more" to fetch next batch from API.
 */
const ProjectListView = ({
  projects = [],
  onProjectClick,
  getProjectName = (p) => p?.project_name ?? p?.name ?? '',
  getUnitStats = (p) => p?.unit_stats ?? {},
  getConstructionStatus = (p) => p?.construction_status ?? p?.status ?? '',
  loadMore,
  loadingMore = false,
  hasMore = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    setPage(0);
  }, [projects.length]);

  const paginatedProjects = projects.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Developer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Units</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProjects.map((p) => {
              const u = getUnitStats(p);
              return (
                <TableRow
                  key={p.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onProjectClick?.(p)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{getProjectName(p)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      {p.location}
                    </Box>
                  </TableCell>
                  <TableCell>{p.developer}</TableCell>
                  <TableCell>
                    {u?.available ?? 0} / {u?.total ?? 0}
                  </TableCell>
                  <TableCell>{getConstructionStatus(p)}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.priority}
                      size="small"
                      color={PRIORITY_COLORS[p.priority] || 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={projects.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage="Rows per page:"
          showFirstButton
          showLastButton
        />
      </TableContainer>
      {projects.length > 0 && (hasMore || loadingMore) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
          {loadingMore && <CircularProgress size={24} />}
          {!loadingMore && hasMore && (
            <Button variant="outlined" size="small" onClick={loadMore}>
              Load more from server
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProjectListView;
